import { type JSX, Show } from "solid-js";
import { Link } from "@tanstack/solid-router";
import { AppBackground } from "@/components/app-background";

/**
 * bodega's OWN auth shell — deliberately NOT the shared `AuthLayout`.
 *
 * Where the generic kit (`AuthLayout`) frames auth as "a glass card centered in
 * a split brand panel" (the shape every clone inherited and which therefore
 * read as a clone), bodega's identity is a single **editorial stacked column**:
 *
 *   ┌──────────────────────────────┐
 *   │  ◳ bodega        · eyebrow ·  │  ← brand band: logo chip + wordmark + chip
 *   │  Big headline.               │
 *   │  Supporting line.            │
 *   │ ───────────────────────────  │  ← hairline rule (no card border)
 *   │  email   [____________]      │  ← form is FULL-BLEED on the ambient bg
 *   │  pass    [____________]      │     (no GlassSurface frame around it)
 *   │  [ Sign in ]                 │
 *   └──────────────────────────────┘
 *
 * No glass surface, no card chrome, no two-column split: the form lives directly
 * on the app background with its own header, so bodega looks structurally unlike
 * the other products even though they share primitives (buttons, fields,
 * SocialAuthButtons, disabled-until-ready). The shell stays app-agnostic about
 * the *form* — screens pour their fields in as `children`.
 *
 * ── SCROLL CONTRACT (same fix as the kit) ─────────────────────────────────
 * `AppBackground` is fixed/overflow-hidden and clips its children, so it is used
 * here as a pure backdrop sibling. The content lives in its own scroll
 * container: `min-h-[100dvh] overflow-y-auto` (never `h-dvh`/`overflow-hidden`),
 * with `min-h-0` inner column, so a tall form (register, or a short landscape
 * viewport) is always fully reachable by scrolling.
 /** Waits for the app to finish booting (skeleton overlay disappears). */

interface AuthSceneProps {
  /** Eyebrow chip (e.g. "Welcome back"). Optional. */
  eyebrow?: string;
  /** Page headline — the editorial promise for this screen. Optional for bare loading states. */
  headline?: string;
  /** Supporting line under the headline. */
  tagline?: string;
  /**
   * Heading level for the headline. The scene headline IS the page h1 for
   * bodega's auth (there is no card title). Pass "h2" only if the screen owns
   * its own h1 elsewhere.
   /** Waits for the app to finish booting (skeleton overlay disappears). */
  headingLevel?: "h1" | "h2";
  /** Width of the column. "narrow" for login/forgot/reset, "wide" for register. */
  width?: "narrow" | "wide";
  /** The form (fields + actions), rendered full-bleed below the brand band. */
  children: JSX.Element;
  /** Footer slot (links: register / back home), rendered under the form. */
  footer?: JSX.Element;
}

export function AuthScene(props: AuthSceneProps) {
  const Heading = (headingProps: { children: JSX.Element }) =>
    (props.headingLevel ?? "h1") === "h2" ? (
      <h2 class="text-pretty text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
        {headingProps.children}
      </h2>
    ) : (
      <h1 class="text-pretty text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
        {headingProps.children}
      </h1>
    );

  return (
    <>
      <AppBackground />

      {/*
        h-[100dvh] (alto FIJO) y no min-h: el root (__root.tsx) monta TODA la app
        dentro de AppBackground (`fixed inset-0 overflow-hidden`), así que un
        contenedor min-h crece más allá del clip y el formulario queda RECORTADO
        sin scroll (regresión #13 en viewports bajos). Con alto fijo + overflow-y-auto
        el scroll ocurre AQUÍ dentro y el form siempre es alcanzable.
      */}
      <div
        class="relative z-10 h-[100dvh] w-full overflow-x-hidden overflow-y-auto px-5 sm:px-6"
        style={{
          "padding-top": "max(2rem, env(safe-area-inset-top))",
          "padding-bottom": "max(2rem, env(safe-area-inset-bottom))",
          "padding-left": "max(1.25rem, env(safe-area-inset-left))",
          "padding-right": "max(1.25rem, env(safe-area-inset-right))",
        }}
      >
        <div
          class="mx-auto flex min-h-full w-full flex-col justify-center py-8"
          classList={{
            "max-w-sm": (props.width ?? "narrow") === "narrow",
            "max-w-md": props.width === "wide",
          }}
        >
          {/* Brand band — logo chip + wordmark + per-screen eyebrow chip. */}
          <header class="flex flex-col gap-5">
            <div class="flex items-center justify-between gap-3">
              <Link
                to="/"
                class="group flex items-center gap-2.5 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                aria-label="Bodega"
              >
                <span class="grid h-10 w-10 place-items-center rounded-xl bg-accent text-accent-foreground shadow-sm ring-1 ring-black/5 transition-transform group-hover:scale-105 group-active:scale-95">
                  <svg
                    class="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M5 12.5l4 4L19 7" />
                  </svg>
                </span>
                <span class="text-lg font-bold tracking-tight text-foreground">bodega</span>
              </Link>
              <Show when={props.eyebrow}>
                <span class="rounded-full bg-accent/15 px-3 py-1 text-xs font-semibold text-accent ring-1 ring-accent/20">
                  {props.eyebrow}
                </span>
              </Show>
            </div>

            <Show when={props.headline}>
              <div class="space-y-2">
                <Heading>{props.headline}</Heading>
                <Show when={props.tagline}>
                  <p class="text-pretty text-base leading-relaxed text-muted-foreground">
                    {props.tagline}
                  </p>
                </Show>
              </div>
            </Show>
          </header>

          {/* Hairline rule instead of a card border. */}
          <div class="my-7 h-px w-full bg-border/70" />

          {/* Form lives full-bleed on the ambient background (no glass card). */}
          <div class="w-full">{props.children}</div>

          <Show when={props.footer}>
            <footer class="mt-7 space-y-3 text-center">{props.footer}</footer>
          </Show>
        </div>
      </div>
    </>
  );
}
